// src/ui/LiveAvatar.jsx — the avatar in a chat row or the chat header (web).
//
// The phone's rules, on the web (src/components/LiveAvatar.tsx there): the
// picture is drawn always; a live view is laid over it only while it is being
// rolled or is allowed to move and is about to — from the shared budget of
// three. It peeks out of the circle when this person comes online (an edge,
// rate-limited), looks around now and then while they stay online, only when
// the row is actually on screen (an IntersectionObserver says so, per row)
// and only when `canSeeOnline` — the same flag the green dot obeys — is true.
// Anyone may roll any avatar with the pointer, online or not.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Avatar3D, { liveViewSize, warmAvatar } from './Avatar3D';
import {
  hasAvatar, avatarMayMove, shouldPeekOnPresence, nextIdleDelay, liveBudget, PRIORITY,
} from '../services/avatar3d';

export const LIVE_LINGER_MS = 1500;

export default function LiveAvatar({ profile, size, viewKey, online = false, canSeeOnline = false, blocked = false, priority = PRIORITY.ROW, still, warm = false, motions = true, style }) {
  const avatar = profile && profile.avatar;
  const enabled = hasAvatar(profile);
  const boxRef = useRef(null);
  const liveRef = useRef(null);
  const [live, setLive] = useState(null);
  const liveReason = useRef(null);
  const pendingMotion = useRef(null);
  const releaseTimer = useRef(null);
  const idleTimer = useRef(null);
  const lastPeekAt = useRef(0);
  const prevOnline = useRef(online);
  const readyRef = useRef(false);
  const visibleRef = useRef(true);
  const dragQueue = useRef(0);

  const mayMove = motions && avatarMayMove({ hasAvatar: enabled, online, canSeeOnline, blocked });

  const avatarId = avatar ? avatar.id : '';
  const avatarAt = avatar ? avatar.updatedAt : 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (warm && enabled) warmAvatar(avatar); }, [warm, enabled, avatarId, avatarAt]);

  // on screen?
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { visibleRef.current = true; return undefined; }
    const io = new IntersectionObserver((entries) => { for (const e of entries) visibleRef.current = e.isIntersecting; }, { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const release = useCallback(() => {
    if (releaseTimer.current) { clearTimeout(releaseTimer.current); releaseTimer.current = null; }
    liveBudget.release(viewKey);
    liveReason.current = null; readyRef.current = false; setLive(null);
  }, [viewKey]);
  const scheduleRelease = useCallback(() => {
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(release, LIVE_LINGER_MS);
  }, [release]);
  const goLive = useCallback((reason) => {
    if (!enabled) return false;
    const pr = reason === 'drag' ? Math.max(priority, PRIORITY.ROW_ACTIVE) : priority;
    if (!liveBudget.acquire(viewKey, pr)) return false;
    if (releaseTimer.current) { clearTimeout(releaseTimer.current); releaseTimer.current = null; }
    liveReason.current = reason; setLive(reason);
    return true;
  }, [enabled, priority, viewKey]);

  useEffect(() => liveBudget.onEvict((key) => { if (key === viewKey) { liveReason.current = null; readyRef.current = false; setLive(null); } }), [viewKey]);
  useEffect(() => () => { liveBudget.release(viewKey); if (releaseTimer.current) clearTimeout(releaseTimer.current); if (idleTimer.current) clearTimeout(idleTimer.current); }, [viewKey]);

  const runMotion = useCallback((kind) => {
    const h = liveRef.current;
    if (!h || !readyRef.current) { pendingMotion.current = kind; return; }
    if (kind === 'peek') h.peek(); else if (kind === 'nod') h.nod(); else if (kind === 'look') h.lookAround(); else h.blink();
  }, []);
  const motion = useCallback((kind) => {
    if (!mayMove || !visibleRef.current) return;
    if (!goLive('motion')) return;
    runMotion(kind);
  }, [mayMove, goLive, runMotion]);

  useEffect(() => {
    const was = prevOnline.current; prevOnline.current = online;
    if (!enabled) return;
    const now = Date.now();
    if (shouldPeekOnPresence(was, online, lastPeekAt.current, now) && mayMove) { lastPeekAt.current = now; motion('peek'); }
  }, [online, enabled, mayMove, motion]);

  useEffect(() => {
    if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
    if (!mayMove) return undefined;
    const tick = () => {
      idleTimer.current = setTimeout(() => {
        const kinds = ['look', 'nod', 'blink', 'look'];
        motion(kinds[Math.floor(Math.random() * kinds.length)]);
        tick();
      }, nextIdleDelay(Math.random()));
    };
    tick();
    return () => { if (idleTimer.current) clearTimeout(idleTimer.current); };
  }, [mayMove, motion]);

  /* the pointer drag — a horizontal move of a few px on the picture is a roll */
  const drag = useRef(null);
  const onPointerDown = (e) => {
    if (!enabled) return;
    drag.current = { x: e.clientX, y: e.clientY, sx: e.clientX, t: performance.now(), vx: 0, active: false };
  };
  const onPointerMove = (e) => {
    const d = drag.current; if (!d) return;
    if (!d.active) {
      const dx = e.clientX - d.sx, dy = e.clientY - d.y;
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { drag.current = null; return; }
      if (Math.abs(dx) < 4) return;
      d.active = true; dragQueue.current = 0;
      e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
      if (liveReason.current !== 'drag') goLive('drag');
      if (releaseTimer.current) { clearTimeout(releaseTimer.current); releaseTimer.current = null; }
    }
    const now = performance.now();
    const step = e.clientX - d.x;
    d.vx = (step / Math.max(1, now - d.t)) * 1000; d.x = e.clientX; d.t = now;
    const h = liveRef.current;
    if (h && readyRef.current) h.dragBy(step); else dragQueue.current += step;
  };
  const onPointerUp = (e) => {
    const d = drag.current; if (!d) return;
    drag.current = null;
    if (!d.active) return;
    e.stopPropagation(); e.preventDefault();
    const h = liveRef.current;
    if (h && readyRef.current) h.dragEnd(d.vx);
    scheduleRelease();
  };
  const onClickCapture = (e) => { if (drag.current === null && liveReason.current === 'drag') { /* a roll is not a click */ e.stopPropagation(); } };

  const onReady = useCallback(() => {
    readyRef.current = true;
    const h = liveRef.current;
    if (h && dragQueue.current) { h.dragBy(dragQueue.current); dragQueue.current = 0; }
    if (pendingMotion.current) { const k = pendingMotion.current; pendingMotion.current = null; runMotion(k); }
    if (liveReason.current === 'motion') scheduleRelease();
  }, [runMotion, scheduleRelease]);
  const onSettled = useCallback(() => { if (liveReason.current === 'motion') scheduleRelease(); }, [scheduleRelease]);
  const onError = useCallback(() => release(), [release]);

  if (!enabled) return still;
  const { height: liveH } = liveViewSize(size);
  return (
    <div
      ref={boxRef}
      className="live-avatar"
      style={{ position: 'relative', width: size, height: size, flexShrink: 0, touchAction: 'pan-y', ...style }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
    >
      {still}
      {live && (
        <div style={{ position: 'absolute', left: 0, bottom: 0, width: size, height: liveH, zIndex: 20, pointerEvents: 'none' }}>
          <Avatar3D
            ref={liveRef}
            avatar={avatar}
            mode="portrait"
            size={size}
            fallback={(profile && profile.profileImage) || (avatar && avatar.snapshotUrl) || null}
            interactive={false}
            returnAfterMs={900}
            onReady={onReady}
            onSettled={onSettled}
            onError={onError}
          />
        </div>
      )}
    </div>
  );
}
